import React from "react";
import { first, map, toUpper } from "lodash";
import { Column, Row } from "@/shared/ui/Blocks";
import { Icon, IconName } from "@/shared/ui/Icon";
import { Title } from "@/shared/ui/Title";
import { InsightCard } from "@/shared/ui/InsightCard";
import { cn } from "@/shared/utils/shadcn";
import { BaseInsight } from "@/shared/hooks/insights/Insight.types";
import { Person } from "@/features/people/graphql/generated";
import { CompetencyInsight } from "@/shared/hooks/insights/useCompetencyInsights";
import { ValuesInsight } from "@/shared/hooks/insights/useValuesInsights";
import { CandidateCompetencyInsight } from "@/shared/hooks/insights/useCandidateCompetencyInsights";

type SupportedInsight =
  | CompetencyInsight
  | ValuesInsight
  | CandidateCompetencyInsight;

interface InsightGroupTitleProps {
  insights: BaseInsight[];
}

/**
 * Renders the group title header with icon and colored background.
 * Uses the first insight's highlight tag for styling.
 */
function InsightGroupTitle({ insights }: InsightGroupTitleProps) {
  if (insights.length === 0) return null;

  const firstInsight = first(insights);
  const tag = firstInsight?.highlight?.tag || firstInsight?.tags?.[0];

  if (!tag) return null;

  return (
    <Row
      className={cn(
        "gap-2 bg-blue-500 px-4 py-1 rounded-full my-8 items-center",
        tag.bg
      )}
    >
      <Icon size="sm" color="white" icon={tag.icon as IconName} />
      <Title as="h6" className="text-white text-sm">
        {toUpper(tag.label)}
      </Title>
    </Row>
  );
}

interface PdfInsightGroupSectionProps {
  insights: BaseInsight[];
  person?: Person;
  columns?: 2 | 3;
}

/**
 * Reusable PDF component for displaying a group of insights with a header and grid layout.
 * Used by both Competencies and Values PDF pages.
 */
function PdfInsightGroupSection({
  insights,
  person,
  columns = 2,
}: PdfInsightGroupSectionProps) {
  if (insights.length === 0) return null;

  const gridClass = columns === 3 ? "grid-cols-3" : "grid-cols-2";

  return (
    <Column>
      <InsightGroupTitle insights={insights} />
      <Row className={cn("gap-4 grid", gridClass)}>
        {map(insights, (insight, index) => (
          <InsightCard
            key={`insight-${insight.dimension.id}-${index}`}
            insight={insight as SupportedInsight}
            person={person}
            width={columns === 3 ? 250 : 400}
          />
        ))}
      </Row>
    </Column>
  );
}

interface PdfInsightsSectionProps {
  groupedInsights: Record<string, BaseInsight[]>;
  person?: Person;
  columns?: 2 | 3;
}

/**
 * Renders multiple insight groups for PDF templates.
 * Maps through grouped insights and renders each group with its header.
 */
function PdfInsightsSection({
  groupedInsights,
  person,
  columns = 2,
}: PdfInsightsSectionProps) {
  return (
    <>
      {map(groupedInsights, (insightGroup, key) => (
        <PdfInsightGroupSection
          key={key}
          insights={insightGroup}
          person={person}
          columns={columns}
        />
      ))}
    </>
  );
}

export { PdfInsightGroupSection, PdfInsightsSection, InsightGroupTitle };
